/**
 * The Registreringer period (Timer, 2026-10-09): I dag, Uke, Måned, or a range of the manager's
 * own (?periode=egen&fra=&til=). A custom range that cannot be a period names the field that is
 * wrong, and the page shows no totals and writes no file until it is right.
 */
import { addDays } from '@timefly/core';

const ISO = /^\d{4}-\d{2}-\d{2}$/;
/** Longest custom range: a year, so one request never pulls years of shifts. */
export const MAX_DAYS = 366;

export type RangeError = { field: 'fra' | 'til'; text: string } | null;

export function rangeError(fra: string | null, til: string | null): RangeError {
  if (!fra || !ISO.test(fra) || Number.isNaN(Date.parse(fra)))
    return { field: 'fra', text: 'Velg en gyldig fra-dato.' };
  if (!til || !ISO.test(til) || Number.isNaN(Date.parse(til)))
    return { field: 'til', text: 'Velg en gyldig til-dato.' };
  if (til < fra) return { field: 'til', text: 'Til må være samme dag som Fra eller senere.' };
  if (addDays(fra, MAX_DAYS - 1) < til) return { field: 'til', text: 'Velg høyst ett år om gangen.' };
  return null;
}

/**
 * A return address on this site, or null: a path that resolves to this origin, with no backslash
 * (browsers read one as a slash, so "/\\evil.com" is another host) and no control character.
 */
export function safeReturn(v: string | null | undefined): string | null {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what it refuses
  if (!v || !v.startsWith('/') || v.startsWith('//') || /[\\\x00-\x1f]/.test(v)) return null;
  try {
    // a page with no web origin (a test, a file) resolves against a stand-in host instead
    const base = /^https?:/.test(window.location.origin) ? window.location.origin : 'http://localhost';
    const u = new URL(v, base);
    return u.origin === new URL(base).origin ? u.pathname + u.search + u.hash : null;
  } catch {
    return null;
  }
}
